<template>
  <div class="p-6">
    <div v-if="searching" class="w-full h-64 bg-grey-light flex justify-center items-center">
      <span><i class="fa fa-spin fa-circle-notch"></i> Loading invitations. Please wait.</span>
    </div>

    <div v-if="!searching && invited_members && invited_members.length > 0">
      <!-- Desktop: a real table so columns size to their content and the page scrolls
           sideways on a narrow window instead of squeezing the email column to nothing -->
      <div class="hidden md:block overflow-x-auto">
        <table class="min-w-full table-auto">
          <thead>
            <tr class="border-b text-left text-xxs uppercase tracking-wide text-grey-dark font-semibold">
              <th class="px-3 py-3 font-semibold">Invitee</th>
              <th class="px-3 py-3 font-semibold">Invited</th>
              <th class="px-3 py-3 font-semibold">Sent</th>
              <th class="px-3 py-3 font-semibold">Expires</th>
              <th class="px-3 py-3 font-semibold">Permission profile</th>
              <th class="px-3 py-3 font-semibold">Joined</th>
              <th class="px-3 py-3 font-semibold">Left</th>
              <th class="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(invitation, index) in invited_members"
              :key="invitation.id"
              class="border-b align-middle"
              :class="{ 'bg-brand-lightest': index % 2 !== 0 }"
            >
              <td class="px-3 py-2 break-words">{{ invitation.email }}</td>
              <td class="px-3 py-2 text-sm">{{ formatDate(invitation.committed_at) }}</td>
              <td class="px-3 py-2 text-sm">{{ formatDate(invitation.sent_at) }}</td>
              <td class="px-3 py-2 text-sm">{{ formatDate(invitation.expires_at) }}</td>
              <td class="px-3 py-2 whitespace-no-wrap">{{ sanitize(invitation.role) }}</td>
              <td class="px-3 py-2 whitespace-no-wrap">
                <template v-if="invitation.is_consumed">
                  <span class="font-medium"><i class="fa fa-check text-green mr-1"></i> Yes</span>
                  <span class="block text-xs text-grey-darker">{{ formatDate(invitation.consumed_at) }}</span>
                </template>
                <span v-else class="text-grey-darker"><i class="fa fa-times text-grey mr-1"></i> No</span>
              </td>
              <td class="px-3 py-2 whitespace-no-wrap">
                <template v-if="invitation.left_at">
                  <span class="font-medium"><i class="fa fa-check text-brand mr-1"></i> Yes</span>
                  <span class="block text-xs text-grey-darker">{{ formatDate(invitation.left_at) }}</span>
                </template>
                <span v-else class="text-grey-darker"><i class="fa fa-times text-grey mr-1"></i> No</span>
              </td>
              <td class="px-3 py-2 whitespace-no-wrap text-right">
                <template v-if="!invitation.is_consumed">
                  <button
                    type="button"
                    class="border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded hover:bg-brand hover:text-white"
                    :class="{ 'opacity-50 cursor-not-allowed': isSending(invitation.id) }"
                    :disabled="isSending(invitation.id)"
                    @click.prevent="sendReminder(invitation)"
                  >
                    <span v-if="isSending(invitation.id)"><i class="fa fa-spinner fa-spin mr-1"></i> Sending</span>
                    <span v-else><i class="fa fa-envelope mr-1"></i> Resend invite</span>
                  </button>
                  <button
                    type="button"
                    class="border border-red bg-white text-red text-sm font-medium py-2 px-4 rounded ml-2 hover:bg-red hover:text-white"
                    @click.prevent="cancelConfirmation(invitation)"
                  >
                    Cancel
                  </button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile: one stacked card per invitation -->
      <div class="md:hidden">
        <div
          v-for="(invitation, index) in invited_members"
          :key="invitation.id"
          class="px-3 py-3 border-b"
          :class="{ 'bg-brand-lightest': index % 2 !== 0 }"
        >
          <p class="break-all font-medium">{{ invitation.email }}</p>
          <p class="text-sm mt-1">{{ sanitize(invitation.role) }}</p>
          <div class="flex flex-wrap text-sm mt-2">
            <div class="w-1/2 py-1">
              <span class="text-xs font-semibold text-grey-darker block">Invited</span>
              {{ formatDate(invitation.committed_at) }}
            </div>
            <div class="w-1/2 py-1">
              <span class="text-xs font-semibold text-grey-darker block">Sent</span>
              {{ formatDate(invitation.sent_at) }}
            </div>
            <div class="w-1/2 py-1">
              <span class="text-xs font-semibold text-grey-darker block">Expires</span>
              {{ formatDate(invitation.expires_at) }}
            </div>
            <div class="w-1/2 py-1">
              <span class="text-xs font-semibold text-grey-darker block">Joined</span>
              <span v-if="invitation.is_consumed"><i class="fa fa-check text-green mr-1"></i> {{ formatDate(invitation.consumed_at) }}</span>
              <span v-else class="text-grey-darker"><i class="fa fa-times text-grey mr-1"></i> No</span>
            </div>
            <div class="w-1/2 py-1" v-if="invitation.left_at">
              <span class="text-xs font-semibold text-grey-darker block">Left</span>
              <i class="fa fa-check text-brand mr-1"></i> {{ formatDate(invitation.left_at) }}
            </div>
          </div>
          <div class="flex mt-2" v-if="!invitation.is_consumed">
            <button
              type="button"
              class="flex-1 border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded"
              :class="{ 'opacity-50 cursor-not-allowed': isSending(invitation.id) }"
              :disabled="isSending(invitation.id)"
              @click.prevent="sendReminder(invitation)"
            >
              <span v-if="isSending(invitation.id)"><i class="fa fa-spinner fa-spin mr-1"></i> Sending</span>
              <span v-else><i class="fa fa-envelope mr-1"></i> Resend invite</span>
            </button>
            <button
              type="button"
              class="flex-1 border border-red bg-white text-red text-sm font-medium py-2 px-4 rounded ml-2"
              @click.prevent="cancelConfirmation(invitation)"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!searching && invited_members && invited_members.length == 0"
      class="w-full h-64 bg-grey-light flex flex-col justify-center items-center"
    >
      <p class="text-grey-darker">No one has been invited to this vault yet.</p>
      <router-link
        :to="{ name: 'invite-members', params: { vault_id: $route.params.vault_id } }"
        class="mt-4 border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded no-underline hover:bg-brand hover:text-white"
      >
        <i class="fa fa-envelope mr-1"></i> Invite members
      </router-link>
    </div>

    <sweet-modal overlay-theme="dark" ref="cancel_invite">
      <div class="flex flex-col" v-if="for_cancellation">
        <p class="font-medium text-lg">Cancel this invitation?</p>
        <p class="mt-2 text-grey-darker">
          {{ for_cancellation.email }} will no longer be able to join with this invitation.
        </p>

        <div class="mt-6 flex justify-center">
          <button
            type="button"
            class="border border-grey bg-white px-6 py-2 text-grey-darker rounded font-medium mr-2 hover:bg-grey-lightest"
            @click.prevent="$refs.cancel_invite.close()"
          >
            Keep invitation
          </button>
          <button
            type="button"
            class="border border-red bg-red px-6 py-2 text-white rounded font-medium hover:bg-red-dark"
            @click.prevent="cancel"
          >
            Cancel invitation
          </button>
        </div>
      </div>
    </sweet-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      invited_members: null,
      for_cancellation: null,
      sending: [], // invitation ids with a reminder request in flight

      searching: false,

      custom_header: {
        title: ["User A"],
      },
    };
  },

  mounted() {
    this.getInvitedMembers();
  },

  methods: {
    getInvitedMembers() {
      this.searching = true;
      axios
        .get(`api/vaults/${this.$route.params.vault_id}/invitations`, {
          params: {
            show_pending: 0,
          },
        })
        .then(({ data }) => {
          this.searching = false;
          this.invited_members = data;
        });
    },
    formatDate(date) {
      if (_.isNil(date)) {
        return "-";
      }
      // Short enough to stay on one line in a 160px column
      return moment(date).format("D MMM YYYY, h:mm A");
    },
    isSending(invitation_id) {
      return this.sending.includes(invitation_id);
    },
    // The request only returns once the email has gone out (sync queue, SMTP), so the
    // button shows a sending state in the meantime and reports failure instead of staying silent.
    sendReminder(invitation) {
      if (this.isSending(invitation.id)) {
        return;
      }
      this.sending.push(invitation.id);
      axios
        .patch(
          `/api/vaults/${this.$route.params.vault_id}/invitations/${invitation.id}/send-mail`
        )
        .then(({ data }) => {
          if (data && data.success === false) {
            this.$noty.error(data.message || "The reminder could not be sent", { killer: true, timeout: 4000 });
            return;
          }
          if (data && data.invitation && data.invitation.sent_at) {
            invitation.sent_at = data.invitation.sent_at;
          }
          this.$noty.success(`Reminder sent to ${invitation.email}`, { killer: true, timeout: 3000 });
        })
        .catch(() => {
          this.$noty.error("The reminder could not be sent", { killer: true, timeout: 4000 });
        })
        .finally(() => {
          this.sending = this.sending.filter((id) => id !== invitation.id);
        });
    },
    cancelConfirmation(invitation) {
      this.for_cancellation = invitation;
      this.$refs.cancel_invite.open();
    },
    cancel() {
      this.$refs.cancel_invite.close();
      axios
        .delete(
          `/api/vaults/${this.$route.params.vault_id}/invitations/${this.for_cancellation.id}`
        )
        .then((resp) => {
          this.getInvitedMembers();
          this.$noty.success("Invitation has been cancelled!", {
            killer: true,
            timeout: 2000,
          });
          this.for_cancellation = null;
        });
    },
    sanitize(role) {
      if (window.vault_type == "strata") {
        return role.title;
      }

      if (window.vault_type == "business") {
        let vault_id = this.current_vault.id;
        let role_name = role.name;
        let replace_char = vault_id + "-";
        let Role = _.trim(role_name.replace(replace_char, " "));

        let index = _.findIndex(this.custom_header.title, (t) => {
          return _.trim(t) == _.trim(Role);
        });

        if (index == -1) {
          return Role;
        } else {
          return role.title.replace(/.+?(\- )/g, "");
        }
      }
    },
  },

  computed: {
    ...mapGetters({
      current_vault: "getCurrentVault",
    }),
    vault_type() {
      return window.vault_type;
    },
  },
};
</script>

<style>
</style>
